import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { NavBar } from 'components/NavBar/NavBar'
import { NoData } from 'components/NoData'
import { Container, ContainerContent, ContainerHeader } from 'components/Containers/Containers'
import { HonestTable, IHonestTableColumn } from 'components/HonestTable/HonestTable'
import { Cell, RightCell } from 'components/HonestTable/HonestTable.styled'
import { ChartTooltip } from 'pages/HrAnalytics/components/ChartTooltip'
import { DemoBadge } from 'pages/Operations/Shared/OpsSharedComponents'
import { LOSS_BUCKET_META } from 'pages/Operations/Shared/lossClassifier'
import { USE_OPS_MOCKS } from 'pages/Operations/Shared/opsControl'
import {
  OPS_LOSS_TREND,
  OPS_PM_TREND,
  OPS_QUEUE_STARVATION,
  OPS_REQUEST_PAIRS,
  OPS_SAFETY_TREND,
  OPS_TRENDS,
} from 'pages/Operations/Shared/opsAnalyticsData'
import { Flex, PageWrapper } from 'styling/GlobalStyles'
import { BodyText, Head1, OtherText, TitleText } from 'styling/Typography'
import { tokens } from 'styling/tokens'
import { IStatusPill } from 'types/ui/statusPill.interface'
import { IOpsTrendPoint, OpsLossBucket } from 'types/opsAnalytics.interface'
import {
  ILossStackPoint,
  ITrendDualPoint,
  ITrendLegendEntry,
  ITrendLegendProps,
  ITrendPairRow,
  ITrendSectionProps,
} from 'types/opsTrendsPage.interface'
import {
  SectionHeaderRow,
  StarvationBars,
  StarvationFill,
  StarvationList,
  StarvationRow,
  StarvationTrack,
  StarvationWeekCol,
  TrendCard,
  TrendLegendItem,
  TrendLegendRow,
  TrendLegendSwatch,
  TrendsGrid,
  ViewDaysLink,
} from './TrendsPage.styled'

const DAY_REPORTS_PATH = '/operations/analytics/reports'

const toneColor = (tone: IStatusPill['status']): string => tokens.status[tone].fg

const AXIS_TICK = { fontSize: 11, fill: tokens.text.muted }

const PRODUCTION_POINTS: IOpsTrendPoint[] =
  OPS_TRENDS.find((series) => series.key === 'production')?.points ?? []

const PRODUCTION_TARGET = PRODUCTION_POINTS[0]?.target ?? null

const AVAILABILITY_POINTS = OPS_TRENDS.find((series) => series.key === 'availability')?.points ?? []
const UTILIZATION_POINTS = OPS_TRENDS.find((series) => series.key === 'utilization')?.points ?? []

const DUAL_POINTS: ITrendDualPoint[] = AVAILABILITY_POINTS.map((point, i) => ({
  label: point.label,
  availability: point.value,
  utilization: UTILIZATION_POINTS[i]?.value ?? 0,
}))

const LOSS_STACK_BUCKETS: OpsLossBucket[] = [
  'BREAKDOWN',
  'NO_TASK',
  'NO_OPERATOR',
  'PLANNED_MAINT',
  'BLOCKED_CROSS_DEPT',
  'WEATHER_BLAST_HOLD',
]

const LOSS_POINTS: ILossStackPoint[] = OPS_LOSS_TREND.map((point) => {
  const row: ILossStackPoint = { label: point.label }
  point.hoursByBucket.forEach((entry) => {
    row[entry.bucket] = entry.hours
  })
  return row
})

const PAIR_ROWS: ITrendPairRow[] = OPS_REQUEST_PAIRS.map((pair, i) => ({ id: i + 1, ...pair }))

const WORST_PAIR = [...OPS_REQUEST_PAIRS].sort((a, b) => b.overduePct - a.overduePct)[0]

const PAIR_CAPTION = WORST_PAIR
  ? `${WORST_PAIR.pair} is the choking pair — ${WORST_PAIR.medianAckMin} min median acknowledge, ${WORST_PAIR.overduePct}% go overdue.`
  : undefined

const PM_WEEK_LABELS = ['Wk 23', 'Wk 24', 'Wk 25', 'Wk 26']

const PM_POINTS: IOpsTrendPoint[] = OPS_PM_TREND.map((value, i) => ({
  label: PM_WEEK_LABELS[i],
  value,
  target: null,
}))

const STARVATION_MAX = Math.max(
  1,
  ...OPS_QUEUE_STARVATION.map((plan) => Math.max(...plan.weeks.map((week) => week.count))),
)

const PRODUCTION_LEGEND: ITrendLegendEntry[] = [
  { label: 'Production (t)', color: tokens.accent.base },
  { label: 'Target 2,100 t', color: tokens.status.danger.fg, dashed: true },
]

const DUAL_LEGEND: ITrendLegendEntry[] = [
  { label: 'Availability %', color: tokens.status.info.fg },
  { label: 'Utilization %', color: tokens.accent.base },
]

const LOSS_LEGEND: ITrendLegendEntry[] = LOSS_STACK_BUCKETS.map((bucket) => ({
  label: LOSS_BUCKET_META[bucket].label,
  color: toneColor(LOSS_BUCKET_META[bucket].tone),
}))

const SAFETY_LEGEND: ITrendLegendEntry[] = [
  { label: 'L1', color: tokens.status.info.fg },
  { label: 'L2', color: tokens.status.warning.fg },
  { label: 'L3', color: tokens.status.danger.fg },
]

const pairColumns: IHonestTableColumn<ITrendPairRow>[] = [
  {
    id: 'pair',
    header: 'Pair',
    minWidth: '14rem',
    cell: (item) => (
      <Cell>
        <BodyText weight='600'>{item.pair}</BodyText>
      </Cell>
    ),
  },
  {
    id: 'volume',
    header: 'Requests',
    align: 'right',
    cell: (item) => (
      <RightCell>
        <BodyText>{item.volume}</BodyText>
      </RightCell>
    ),
  },
  {
    id: 'medianAckMin',
    header: 'Median time to acknowledge',
    align: 'right',
    cell: (item) => (
      <RightCell>
        <BodyText>{item.medianAckMin} min</BodyText>
      </RightCell>
    ),
  },
  {
    id: 'overduePct',
    header: '% overdue',
    align: 'right',
    cell: (item) => (
      <RightCell>
        <BodyText weight='600' color={item.overduePct > 25 ? tokens.status.danger.fg : undefined}>
          {item.overduePct}%
        </BodyText>
      </RightCell>
    ),
  },
]

const TrendLegend = ({ items }: ITrendLegendProps) => (
  <TrendLegendRow>
    {items.map((item) => (
      <TrendLegendItem key={item.label}>
        <TrendLegendSwatch $color={item.color} $dashed={item.dashed} />
        {item.label}
      </TrendLegendItem>
    ))}
  </TrendLegendRow>
)

const TrendSection = ({ title, caption, captionColor, link, full, children }: ITrendSectionProps) => (
  <TrendCard $full={full}>
    <ContainerHeader padding>
      <SectionHeaderRow>
        <Flex column gap='0.25rem'>
          <TitleText weight='bold'>{title}</TitleText>
          {caption && (
            <OtherText grey={!captionColor} color={captionColor}>
              {caption}
            </OtherText>
          )}
        </Flex>
        {link && <ViewDaysLink to={link}>View days ›</ViewDaysLink>}
      </SectionHeaderRow>
    </ContainerHeader>
    <ContainerContent gap='0.5rem'>{children}</ContainerContent>
  </TrendCard>
)

export const TrendsPage = () => {
  return (
    <>
      <NavBar />
      <PageWrapper>
        <Flex column gap='0.25rem'>
          <Flex center gap='0.75rem'>
            <Head1 style={{ fontSize: '1.5rem', lineHeight: 1.3 }} color={tokens.text.primary}>
              Trends
            </Head1>
            <DemoBadge />
          </Flex>
          <BodyText grey>30 days · day shifts only — is it noise or a pattern?</BodyText>
        </Flex>
        {USE_OPS_MOCKS ? (
          <TrendsGrid>
            <TrendSection
              title='Production vs target'
              caption='Slipping ~40 t per shift since 20 Jun.'
              link={DAY_REPORTS_PATH}
            >
              <ResponsiveContainer width='100%' height={240}>
                <AreaChart data={PRODUCTION_POINTS}>
                  <defs>
                    <linearGradient id='trendProductionFill' x1='0' y1='0' x2='0' y2='1'>
                      <stop offset='0%' stopColor={tokens.accent.base} stopOpacity={0.35} />
                      <stop offset='100%' stopColor={tokens.accent.base} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray='3 3' stroke={tokens.border.hairline} />
                  <XAxis dataKey='label' tick={AXIS_TICK} tickLine={false} />
                  <YAxis domain={[1700, 2200]} tick={AXIS_TICK} tickLine={false} />
                  <Tooltip
                    cursor={{ stroke: tokens.border.strong, strokeWidth: 1 }}
                    content={<ChartTooltip formatter={(value: number) => `${value.toLocaleString()} t`} />}
                  />
                  {PRODUCTION_TARGET != null && (
                    <ReferenceLine
                      y={PRODUCTION_TARGET}
                      stroke={tokens.status.danger.fg}
                      strokeDasharray='8 8'
                      strokeWidth={2}
                    />
                  )}
                  <Area
                    type='monotone'
                    dataKey='value'
                    name='Production'
                    stroke={tokens.accent.base}
                    strokeWidth={2}
                    fill='url(#trendProductionFill)'
                    dot={false}
                    activeDot={{ r: 4, fill: tokens.accent.base }}
                  />
                </AreaChart>
              </ResponsiveContainer>
              <TrendLegend items={PRODUCTION_LEGEND} />
            </TrendSection>
            <TrendSection
              title='Availability vs utilization'
              caption='Availability dips are machines breaking — utilization sag is machines standing.'
              link={DAY_REPORTS_PATH}
            >
              <ResponsiveContainer width='100%' height={240}>
                <LineChart data={DUAL_POINTS}>
                  <CartesianGrid strokeDasharray='3 3' stroke={tokens.border.hairline} />
                  <XAxis dataKey='label' tick={AXIS_TICK} tickLine={false} />
                  <YAxis domain={[50, 100]} tick={AXIS_TICK} tickLine={false} />
                  <Tooltip
                    cursor={{ stroke: tokens.border.strong, strokeWidth: 1 }}
                    content={<ChartTooltip formatter={(value: number) => `${value}%`} />}
                  />
                  <Line
                    type='monotone'
                    dataKey='availability'
                    name='Availability'
                    stroke={tokens.status.info.fg}
                    strokeWidth={2}
                    dot={false}
                    activeDot={{ r: 4, fill: tokens.status.info.fg }}
                  />
                  <Line
                    type='monotone'
                    dataKey='utilization'
                    name='Utilization'
                    stroke={tokens.accent.base}
                    strokeWidth={2}
                    dot={false}
                    activeDot={{ r: 4, fill: tokens.accent.base }}
                  />
                </LineChart>
              </ResponsiveContainer>
              <TrendLegend items={DUAL_LEGEND} />
            </TrendSection>
            <TrendSection
              full
              title='Where hours are lost'
              caption='Breakdown share grew 8% → 14% in a month.'
              link={DAY_REPORTS_PATH}
            >
              <ResponsiveContainer width='100%' height={260}>
                <BarChart data={LOSS_POINTS}>
                  <CartesianGrid strokeDasharray='3 3' stroke={tokens.border.hairline} />
                  <XAxis dataKey='label' tick={AXIS_TICK} tickLine={false} />
                  <YAxis tick={AXIS_TICK} tickLine={false} />
                  <Tooltip
                    cursor={{ fill: tokens.surface.inset }}
                    content={<ChartTooltip formatter={(value: number) => `${value} h`} />}
                  />
                  {LOSS_STACK_BUCKETS.map((bucket) => (
                    <Bar
                      key={bucket}
                      dataKey={bucket}
                      name={LOSS_BUCKET_META[bucket].label}
                      stackId='loss'
                      fill={toneColor(LOSS_BUCKET_META[bucket].tone)}
                    />
                  ))}
                </BarChart>
              </ResponsiveContainer>
              <TrendLegend items={LOSS_LEGEND} />
            </TrendSection>
            <TrendSection full title='Coordination health' caption={PAIR_CAPTION}>
              <HonestTable columns={pairColumns} data={PAIR_ROWS} keyField='id' />
            </TrendSection>
            <TrendSection
              title='Queue starvation'
              caption='North Pit queue ran dry 5 times in Wk 26 — climbing every week.'
            >
              <StarvationList>
                {OPS_QUEUE_STARVATION.map((plan) => (
                  <StarvationRow key={plan.planId}>
                    <Flex column gap='0.25rem'>
                      <BodyText weight='600'>{plan.planName}</BodyText>
                      <OtherText grey>
                        {plan.weeks.reduce((total, week) => total + week.count, 0)} starvation events in 4 weeks
                      </OtherText>
                    </Flex>
                    <StarvationBars>
                      {plan.weeks.map((week) => (
                        <StarvationWeekCol key={week.label}>
                          <OtherText weight='600'>{week.count}</OtherText>
                          <StarvationTrack>
                            <StarvationFill $pct={(week.count / STARVATION_MAX) * 100} />
                          </StarvationTrack>
                          <OtherText grey>{week.label}</OtherText>
                        </StarvationWeekCol>
                      ))}
                    </StarvationBars>
                  </StarvationRow>
                ))}
              </StarvationList>
            </TrendSection>
            <TrendSection
              title='Safety'
              caption='First L3 in a month landed in Wk 26.'
              link={DAY_REPORTS_PATH}
            >
              <ResponsiveContainer width='100%' height={200}>
                <BarChart data={OPS_SAFETY_TREND} barCategoryGap='24%'>
                  <CartesianGrid strokeDasharray='3 3' stroke={tokens.border.hairline} />
                  <XAxis dataKey='label' tick={AXIS_TICK} tickLine={false} />
                  <YAxis allowDecimals={false} tick={AXIS_TICK} tickLine={false} />
                  <Tooltip cursor={{ fill: tokens.surface.inset }} content={<ChartTooltip />} />
                  <Bar dataKey='l1' name='L1' fill={tokens.status.info.fg} radius={[3, 3, 0, 0]} />
                  <Bar dataKey='l2' name='L2' fill={tokens.status.warning.fg} radius={[3, 3, 0, 0]} />
                  <Bar dataKey='l3' name='L3' fill={tokens.status.danger.fg} radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
              <TrendLegend items={SAFETY_LEGEND} />
            </TrendSection>
            <TrendSection
              title='PM compliance'
              caption='82% and falling — four straight weeks of slide.'
              captionColor={tokens.status.warning.fg}
            >
              <ResponsiveContainer width='100%' height={160}>
                <LineChart data={PM_POINTS}>
                  <CartesianGrid strokeDasharray='3 3' stroke={tokens.border.hairline} vertical={false} />
                  <XAxis dataKey='label' tick={AXIS_TICK} tickLine={false} />
                  <YAxis
                    domain={[70, 100]}
                    tick={AXIS_TICK}
                    tickLine={false}
                    tickFormatter={(value) => `${value}%`}
                  />
                  <Tooltip
                    cursor={{ stroke: tokens.border.strong, strokeWidth: 1 }}
                    content={<ChartTooltip formatter={(value: number) => `${value}%`} />}
                  />
                  <Line
                    type='monotone'
                    dataKey='value'
                    name='PM compliance'
                    stroke={tokens.status.warning.fg}
                    strokeWidth={2}
                    dot={{ r: 3, fill: tokens.status.warning.fg }}
                    activeDot={{ r: 4, fill: tokens.status.warning.fg }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </TrendSection>
          </TrendsGrid>
        ) : (
          <Flex mt='32' width='100%'>
            <Container>
              <ContainerContent>
                <NoData text='Trends unlock once shift aggregates exist.' />
              </ContainerContent>
            </Container>
          </Flex>
        )}
      </PageWrapper>
    </>
  )
}
